import React, { useEffect, useState } from "react";
import { FileUploader } from "react-drag-drop-files";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
    addOrUpdateStoreAuditData,
    getStoreAuditData,
} from "../../../../Redux/Slices/Accessor/accessorSlice";
import { multiUpload } from "../../../../Redux/Slices/User/userSlice";
import ReactQuill from "react-quill-new";
import "quill/dist/quill.snow.css";
import InputBox from "Components/InputBox";
import ImageModal from "Components/ImageModal";
import Loader from "Components/Loader";
import Button from "Components/Button";
import { AiOutlineDelete } from "react-icons/ai";
import { MdDeleteOutline } from "react-icons/md";
import uploadIcon from "../../../../Assets/Images/uploadIcon.svg";
import { compressImage } from "Services/Utils/imageCompression";
import { notifySuccess } from "Services/Utils/toastNotification";
import BackArrow from "../../../../Assets/Svg/Auth/BackArrow.svg";
import "./styles.scss";
import MainLayout from "Layouts/Main";

const SparePartsAudit = () => {
    const [spareParts, setSpareParts] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const location = useLocation();
    const backlog_id = location.state.backlog_id;

    const dispatch = useDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        setIsLoading(true);

        dispatch(
            getStoreAuditData({
                backlog_id,
                callBack: (list) => {
                    const formatted = (list || []).map((item) => ({
                        product_name: item.product_name || "",
                        photos: (item.photos || []).map((url) => ({
                            name: url.split("/").pop(),
                            url,
                        })),
                        uploadError: "",
                    }));

                    setSpareParts(
                        formatted.length > 0
                            ? formatted
                            : [{ product_name: "", photos: [], uploadError: "" }]
                    );
                    setIsLoading(false);
                },
            })
        )
            .unwrap()
            .catch(() => {
                setSpareParts([{ product_name: "", photos: [], uploadError: "" }]);
                setIsLoading(false);
            });
    }, [backlog_id, dispatch]);

    const updateField = (index, field, value) => {
        setSpareParts((prev) => {
            const updated = [...prev];
            updated[index][field] = value;
            return updated;
        });
    };

    const addEntry = () => {
        setSpareParts((prev) => [
            ...prev,
            { product_name: "", photos: [], uploadError: "" },
        ]);
    };

    const deleteEntry = (index) => {
        setSpareParts((prev) => prev.filter((_, i) => i !== index));
    };

    const handleFileChange = async (fileList, index) => {
        const validExtensions = [".png", ".jpg", ".jpeg"];
        const MAX_FILE_SIZE = 5 * 1024 * 1024;

        const files = Array.from(fileList);
        const compressedFiles = [];
        let errorMessage = "";

        for (const file of files) {
            const ext = "." + file.name.split(".").pop().toLowerCase();
            if (!validExtensions.includes(ext)) {
                errorMessage = "Invalid file type (only PNG/JPG/JPEG allowed)";
                break;
            }
            if (file.size > MAX_FILE_SIZE) {
                errorMessage = `File ${file.name} exceeds 5 MB limit`;
                break;
            }

            let finalFile = file;
            if (file.size > 1 * 1024 * 1024) {
                try {
                    const compressedBlob = await compressImage(file, 1);
                    finalFile = new File([compressedBlob], file.name, {
                        type: compressedBlob.type,
                        lastModified: Date.now(),
                    });
                } catch {
                    errorMessage = "Image compression failed";
                }
            }

            compressedFiles.push(finalFile);
        }

        if (errorMessage) {
            updateField(index, "uploadError", errorMessage);
            return;
        }

        updateField(index, "uploadError", "");

        const formData = new FormData();
        compressedFiles.forEach((f) => formData.append("files", f));

        dispatch(
            multiUpload({
                files: formData,
                callBack: (uploadedFiles) => {
                    const newPhotos = uploadedFiles.map((file) => ({
                        name: file.originalName || file.name,
                        url: file.url,
                    }));
                    setSpareParts(prev => {
                        const updated = [...prev];
                        updated[index].photos = [
                            ...updated[index].photos,
                            ...newPhotos
                        ];
                        return updated;
                    });
                },
            })
        );
    };

    const handleSubmit = () => {
        const payload = {
            backlog_id,
            auditsData: spareParts.map((item) => ({
                product_name: item.product_name,
                photos: item.photos.map((p) => p.url),
            })),
        };

        dispatch(
            addOrUpdateStoreAuditData({
                data: payload,
                callBack: () => {
                    notifySuccess("Successfully saved!");
                    navigate("/store-audit");
                },
            })
        );
    };

    return (
        <MainLayout>
            <div className="SparePartUploaderWrap my-4 p-2 position-relative">
                {isLoading && (
                    <div className="full-page-loader">
                        <Loader />
                    </div>
                )}

                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="d-flex align-items-center">
                        <img src={BackArrow} width="16" alt="Back" style={{ cursor: "pointer" }} onClick={() => navigate("/store-audit")} />
                        <h4 style={{ paddingLeft: "12px", marginTop: "2px" }}>
                            {/* Spare Parts */}
                            Store Audit
                            </h4>
                    </div>
                    <Button
                        onClick={addEntry}
                        title="Add"
                        titleStyle={{ fontSize: "14px", fontWeight: 600 }}
                        buttonStyle={{ width: "60px", height: "36px" }}
                    />
                </div>

                {!isLoading &&
                    spareParts.map((item, index) => (
                        <div key={index} className="sparePartUploaderContainer my-3 p-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <h5>{index + 1}. Spare Part</h5>
                                <MdDeleteOutline
                                    onClick={() => deleteEntry(index)}
                                    size={25}
                                    className="removeIcon"
                                />
                            </div>

                            {/* Product Name */}
                            <div className="col-md-8 my-3">
                                    <label
                                        style={{
                                        color: "#2E2E2E",
                                        fontWeight: 600,
                                        fontSize: "16px",
                                        marginBottom: "6px",
                                        }}
                                    >
                                        Product Name
                                    </label>

                                    <ReactQuill
                                        theme="snow"
                                        value={item.product_name}
                                        onChange={(value) =>
                                        updateField(index, "product_name", value)
                                        }
                                        placeholder="Enter product name..."
                                        className="styled-quill-editor"
                                        modules={{
                                        toolbar: [
                                            ["bold", "italic", "underline"],
                                            [{ list: "ordered" }, { list: "bullet" }],
                                            ["clean"],
                                        ],
                                        }}
                                        formats={[
                                        "bold",
                                        "italic",
                                        "underline",
                                        "list",
                                        "bullet",
                                        ]}
                                    />
                                    </div>

                            {/* <div className="col-md-8 my-3">
                                <InputBox
                                    label="Product Name"
                                    value={item.product_name}
                                    onChange={(e) =>
                                        updateField(index, "product_name", e.target.value)
                                    }
                                    inputStyle={{
                                        border: "1px solid #858485",
                                        borderRadius: "4px",
                                        height: "49px",
                                        fontSize: "14px",
                                        fontWeight: "400"
                                    }}
                                    labelStyle={{
                                        color: "#2E2E2E",
                                        fontWeight: "600",
                                        fontSize: "16px"
                                    }}
                                />
                            </div> */}

                            {/* Image Upload */}
                            <div className="col-md-8">
                                <label className="uploadLabel">Image</label>
                                <div className="filess">
                                    <FileUploader
                                        multiple
                                        handleChange={(file) => handleFileChange(file, index)}
                                        name="file"
                                        classes="drop_area drop_zone"
                                    >
                                        <p className="drag">
                                            <img src={uploadIcon} className="mx-2" alt="" /> Drag or{" "}
                                            <span className="here mx-2">Browse</span>
                                        </p>
                                    </FileUploader>
                                    <p className="uploadNote">Note: Only images (JPG, PNG, JPEG) allowed. Max size: 5MB.</p>
                                    {item.uploadError && <p className="text-danger">{item.uploadError}</p>}
                                </div>

                                {/* Image Preview */}
                                <div className="uploaded-file-list">
                                    {item.photos.length > 0 ?
                                        item.photos.map((photo, i) => (
                                            <div key={i} className="uploaded-image-preview">
                                                {/* <img
                                                    src={photo.url}
                                                    alt={photo.name}
                                                    className="preview-img"
                                                /> */}
                                                <ImageModal src={photo.url} alt={photo.name} />
                                                <div className="uploaded-file-name">
                                                    <span>{photo.name}</span>
                                                    <AiOutlineDelete
                                                        onClick={() =>
                                                            updateField(
                                                                index,
                                                                "photos",
                                                                item.photos.filter((_, idx) => idx !== i)
                                                            )
                                                        }
                                                        size={20}
                                                        className="remove-icon"
                                                    />
                                                </div>
                                            </div>
                                        )) : <p>No image uploaded yet.</p>}
                                </div>

                            </div>
                        </div>
                    ))}

                {!isLoading && (
                    <div className="d-flex justify-content-end">
                        <Button
                            title="Submit"
                            titleStyle={{ fontWeight: 600, fontSize: "20px" }}
                            buttonStyle={{
                                height: "44px",
                                border: "1px solid #1f2155",
                                width: "250px",
                            }}
                            onClick={handleSubmit}
                        />
                    </div>
                )}

            </div>
        </MainLayout>
    );
};

export default SparePartsAudit;
